import { Cancel, Check } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';

const getDuration = (tempo_apontado) => {
  const duration = moment.duration(tempo_apontado, 'seconds');

  const horas = Math.floor(duration.asHours());
  const minutos = duration.minutes();
  const segundos = duration.seconds();

  const tempoFormatado = `${String(horas).padStart(2, '0')}h ${String(minutos).padStart(2, '0')}m ${String(segundos).padStart(2, '0')}s`;

  return tempoFormatado;
};

const StatusCell = ({ row = {} }) => {
  const backgroundColor =
    row?.status === 'CONCLUIDO'
      ? '#7FFF7F'
      : row?.status === 'PENDENTE'
        ? '#FFFF7F'
        : row?.status === 'CANCELADO'
          ? '#FF7F7F'
          : 'transparent';

  return (
    <Box flex={1} display="flex">
      <Chip
        size="small"
        label={row?.status}
        sx={{ flex: 1, backgroundColor, color: 'text.primary' }}
      />
    </Box>
  );
};

const DocumentoCell = ({ row = {} }) => {
  const lines = [
    {
      label: 'Data de Emissão',
      value: row?.dtemissao && moment(row?.dtemissao).format('DD/MM/YYYY'),
    },
    {
      label: 'Data de Entrega',
      value: row?.przent && moment(row?.przent).format('DD/MM/YYYY'),
    },
    {
      label: 'Origem',
      value: row?.origem,
    },
    {
      label: 'Lote de Cálculo',
      value: row?.producao_calculo_id,
    },
    {
      label: 'Tipo',
      value: row?.tipo,
    },
    {
      label: 'Cliente',
      value: row?.cliente,
    },
  ];

  return (
    <Box flex={1} my={1} alignSelf="flex-start">
      {Boolean(row?.documento) && (
        <Typography fontWeight="bold" mb={1} variant="body1">
          Nº {row?.documento}
        </Typography>
      )}
      {lines?.map(
        ({ label, value }, i) =>
          Boolean(value) && (
            <Box key={i?.toString()} display="flex">
              <Typography flex={1} variant="caption" fontWeight="bold">
                {label}:{' '}
              </Typography>
              <Typography flex={2} variant="caption">
                {value}
              </Typography>
            </Box>
          )
      )}
    </Box>
  );
};

const ProdutoCell = ({ row = {} }) => {
  const lines = [
    {
      label: 'Referência',
      value: row?.referencia,
    },
    {
      label: 'Linha',
      value: row?.linha,
    },
    {
      label: 'Grupo',
      value: row?.grupo,
    },
    {
      label: 'Aplicação',
      value: row?.aplicacao,
    },
    {
      label: 'Produto Base',
      value: row?.produtobase,
    },
  ];

  return (
    <Box flex={1} my={1} alignSelf="flex-start">
      <Typography fontWeight="bold" mb={1} variant="body1">
        {row?.produto}
      </Typography>
      {lines?.map(
        ({ label, value }, i) =>
          Boolean(value) && (
            <Box key={i?.toString()} display="flex">
              <Typography flex={1} variant="caption" fontWeight="bold">
                {label}:{' '}
              </Typography>
              <Typography flex={2} variant="caption">
                {value}
              </Typography>
            </Box>
          )
      )}
    </Box>
  );
};

const ProcessoCell = ({ row = {} }) => {
  const processo = row?.processo;
  const lines = [
    {
      label: 'Centro de Custo',
      value: row?.ccusto,
    },
    {
      label: 'Tempo Apontado',
      value: processo?.tempo_apontado && getDuration(processo?.tempo_apontado),
    },
  ];

  return (
    <Box flex={1} my={1} alignSelf="flex-start">
      <Typography fontWeight="bold" mb={1} variant="body1">
        {processo?.operacao && `${processo?.operacao} - `} {processo?.descricao}
      </Typography>
      {lines?.map(
        ({ label, value }, i) =>
          Boolean(value) && (
            <Box key={i?.toString()} display="flex">
              <Typography flex={1} variant="caption" fontWeight="bold">
                {label}:{' '}
              </Typography>
              <Typography flex={1} variant="caption">
                {value}
              </Typography>
            </Box>
          )
      )}
    </Box>
  );
};

const EstoqueCell = ({ row = {} }) => {
  const quantidade_produzir = Number(row?.quantidade_produzir || 0);
  const quantidade_produzida = Number(row?.quantidade_produzida || 0);
  const percentual =
    quantidade_produzir > 0
      ? (quantidade_produzida / quantidade_produzir) * 100
      : 0;
  const lines = [
    {
      label: 'Qtd. Produzida',
      value: quantidade_produzida + ' ' + row?.unidade_medida,
    },
    {
      label: 'Qtd. a Produzir',
      value: quantidade_produzir + ' ' + row?.unidade_medida,
    },
  ];

  return (
    <Box
      my={1}
      flex={1}
      display="flex"
      alignSelf="flex-start"
      flexDirection="column"
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="body2" fontWeight="bold">
          {new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(
            percentual
          )}
          %
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Produzido
        </Typography>
      </Box>
      {lines?.map(
        ({ label, value }, i) =>
          Boolean(value) && (
            <Box key={i?.toString()} display="flex">
              <Typography flex={2} variant="caption" fontWeight="bold">
                {label}:{' '}
              </Typography>
              <Typography flex={1} variant="caption">
                {value}
              </Typography>
            </Box>
          )
      )}
    </Box>
  );
};

const MateriaisCell = ({ row = {} }) => {
  const percentual_requisitado = Number(row?.materiais?.pcrequisitado || 0);
  const percentual_movimentado = Number(row?.materiais?.pcmovimentado || 0);
  const disponivel = Boolean(row?.materiais?.disponibilidade === 'Disponível');
  const chipColor = disponivel ? 'success' : 'error';
  const chipIcon = disponivel ? (
    <Check fontSize="small" />
  ) : (
    <Cancel fontSize="small" />
  );
  const chipLabel = disponivel
    ? 'Disponível para Requisitar'
    : 'Indisponível para Requisitar';

  return (
    <Box
      my={1}
      gap={1}
      flex={1}
      display="flex"
      alignSelf="flex-start"
      flexDirection="column"
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight="bold">
          {percentual_requisitado.toFixed(0)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Requisitado
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight="bold">
          {percentual_movimentado.toFixed(0)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Movimentado
        </Typography>
      </Box>
      {percentual_requisitado < 100 && (
        <Chip
          size="small"
          variant="outlined"
          label={chipLabel}
          color={chipColor}
          icon={chipIcon}
          sx={{ width: 'fit-content', '& .MuiChip-label': { fontSize: 11 } }}
        />
      )}
    </Box>
  );
};

const columnOverrides = [
  {
    field: 'status',
    renderCell: (props) => <StatusCell {...props} />,
  },
  {
    field: 'documento',
    renderCell: (props) => <DocumentoCell {...props} />,
  },
  {
    field: 'produto',
    renderCell: (props) => <ProdutoCell {...props} />,
  },
  {
    field: 'processo',
    renderCell: (props) => <ProcessoCell {...props} />,
  },
  {
    field: 'quantidade_produzir',
    renderCell: (props) => <EstoqueCell {...props} />,
  },
  {
    field: 'materiais',
    renderCell: (props) => <MateriaisCell {...props} />,
  },
  // {
  //   field: 'dtemissao',
  //   valueGetter: ({ value }) => value && moment(value).format('DD/MM/YYYY'),
  // },
  // {
  //   field: 'przent',
  //   valueGetter: ({ value }) => value && moment(value).format('DD/MM/YYYY'),
  // },
  // {
  //   field: 'materiais',
  //   renderCell: ({ row }) => {
  //     const percentual = Number(row?.materiais?.pcrequisitado || 0);
  //     const disponivel = Boolean(
  //       row?.materiais?.disponibilidade === 'Disponível'
  //     );
  //     return (
  //       <Box my={1} gap={1} flex={1} display="flex" flexDirection="column">
  //         <Box display="flex" alignItems="center" gap={1}>
  //           <Typography variant="body2" fontWeight="bold">
  //             {percentual.toFixed(0)}%
  //           </Typography>
  //           <Typography variant="caption" color="text.secondary">
  //             Requisitado
  //           </Typography>
  //         </Box>
  //         {percentual < 100 && (
  //           <Chip
  //             size="small"
  //             variant="outlined"
  //             label={
  //               disponivel
  //                 ? 'Disponível para Requisitar'
  //                 : 'Indisponível para Requisitar'
  //             }
  //             color={disponivel ? 'success' : 'error'}
  //             icon={
  //               disponivel ? (
  //                 <Check fontSize="small" />
  //               ) : (
  //                 <Cancel fontSize="small" />
  //               )
  //             }
  //             sx={{
  //               width: 'fit-content',
  //               '& .MuiChip-label': {
  //                 fontSize: 11,
  //               },
  //             }}
  //           />
  //         )}
  //       </Box>
  //     );
  //   },
  // },
];

export default columnOverrides;
